import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Search, Star } from 'lucide-react';
import { getBibleChapter, listBibleBooks, searchBible, toggleFavorite } from '../../../utils/spiritualApi';
import { findBibleBook, parseBibleReference } from '../../../utils/bibleReference';
import SpiritualBibleHowTo from './SpiritualBibleHowTo';
import './BibleReaderPanel.css';

export default function BibleReaderPanel({ onBack }) {
  const [books, setBooks] = useState([]);
  const [booksLoading, setBooksLoading] = useState(true);
  const [booksError, setBooksError] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [chapterCount, setChapterCount] = useState(0);
  const [chapter, setChapter] = useState(null);
  const [chapterNum, setChapterNum] = useState(1);
  const [highlightVerse, setHighlightVerse] = useState(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searchError, setSearchError] = useState('');
  const [busy, setBusy] = useState(false);
  const verseRefs = useRef({});

  useEffect(() => {
    setBooksLoading(true);
    setBooksError(null);
    listBibleBooks()
      .then((rows) => setBooks(Array.isArray(rows) ? rows : []))
      .catch(() => {
        setBooks([]);
        setBooksError('No se pudo cargar la Biblia. Verifica que el backend esté activo.');
      })
      .finally(() => setBooksLoading(false));
  }, []);

  useEffect(() => {
    if (!chapter || highlightVerse == null) return;
    const el = verseRefs.current[highlightVerse];
    if (el?.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [chapter, highlightVerse]);

  const loadChapter = async (book, num, verseToHighlight = null) => {
    setBusy(true);
    setSearchError('');
    try {
      const data = await getBibleChapter(book.code, num);
      setSelectedBook(book);
      setChapter(data);
      setChapterNum(num);
      setChapterCount(Number(data?.chapter_count) || chapterCount || 1);
      setHighlightVerse(verseToHighlight);
      setResults([]);
    } catch {
      setSearchError('No se pudo cargar el capítulo.');
    } finally {
      setBusy(false);
    }
  };

  const onSelectBook = async (book) => {
    setBusy(true);
    setResults([]);
    setChapter(null);
    setHighlightVerse(null);
    setSearchError('');
    setSelectedBook(book);
    try {
      const data = await getBibleChapter(book.code, 1);
      const total = Number(data?.chapter_count) || 1;
      setChapterCount(total);
      setChapterNum(1);
      // Solo lista de capítulos; el texto se carga al elegir capítulo.
    } catch {
      setChapterCount(0);
      setSearchError('No se pudieron cargar los capítulos de este libro.');
    } finally {
      setBusy(false);
    }
  };

  const onSelectChapter = (num) => {
    if (!selectedBook) return;
    loadChapter(selectedBook, num, null);
  };

  const goToBooks = () => {
    setSelectedBook(null);
    setChapter(null);
    setChapterCount(0);
    setHighlightVerse(null);
    setResults([]);
    setSearchError('');
  };

  const goToChapters = () => {
    setChapter(null);
    setHighlightVerse(null);
    setResults([]);
  };

  const openReference = async (parsed) => {
    const book = findBibleBook(books, parsed.bookQuery);
    if (!book) {
      setSearchError(`No encontré el libro «${parsed.bookQuery}».`);
      return false;
    }
    await loadChapter(book, parsed.chapter, parsed.verse);
    return true;
  };

  const onSearch = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setSearchError('');
    setBusy(true);
    try {
      const parsed = parseBibleReference(q);
      if (parsed) {
        const ok = await openReference(parsed);
        if (ok) return;
      }
      const rows = await searchBible(q);
      setResults(Array.isArray(rows) ? rows : []);
      setSelectedBook(null);
      setChapter(null);
      setChapterCount(0);
      if (!rows?.length) {
        setSearchError('Sin resultados. Prueba «Juan 3:16» o una palabra del texto.');
      }
    } catch {
      setSearchError('No se pudo buscar.');
    } finally {
      setBusy(false);
    }
  };

  const openSearchHit = async (r) => {
    const book = books.find((b) => b.code === r.book_code)
      || findBibleBook(books, r.reference?.split(/\s+\d/)[0] || '');
    if (!book) return;
    await loadChapter(book, Number(r.chapter_number) || 1, Number(r.verse_number) || null);
  };

  const isOldTestament = (book) => {
    const t = String(book.testament || '').toUpperCase();
    if (t === 'NT') return false;
    if (t === 'OT') return true;
    return Number(book.orden) > 0 && Number(book.orden) < 40;
  };
  const oldTestament = books.filter(isOldTestament);
  const newTestament = books.filter((book) => !isOldTestament(book));

  const renderBookGrid = (rows) => (
    <div className="bible-reader-panel__book-grid">
      {rows.map((b) => (
        <button
          key={b.id}
          type="button"
          className="bible-reader-panel__book-btn"
          onClick={() => onSelectBook(b)}
          disabled={busy}
        >
          {b.name}
        </button>
      ))}
    </div>
  );

  const showBooks = !selectedBook && !results.length;
  const showChapters = selectedBook && !chapter && chapterCount > 0;
  const showVerses = selectedBook && chapter;

  return (
    <div className="dashboard-main-view bible-reader-panel space-y-4">
      <header className="flex items-center gap-3">
        {onBack ? (
          <button type="button" className="btn-secondary" onClick={onBack}>
            <ArrowLeft className="mr-1 inline h-4 w-4" />
            Volver
          </button>
        ) : null}
        <h2 className="bible-reader-panel__title">Biblia Reina-Valera 1960</h2>
      </header>
      <SpiritualBibleHowTo />

      <form onSubmit={onSearch} className="flex gap-2">
        <input
          className="input flex-1"
          placeholder="Ej. Juan 3:16 · Génesis 1 · amor"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Buscar libro, capítulo y versículo"
        />
        <button type="submit" className="btn-primary" disabled={busy}>
          <Search className="h-4 w-4" />
        </button>
      </form>
      {searchError ? <p className="bible-reader-panel__hint bible-reader-panel__hint--err">{searchError}</p> : null}
      <p className="bible-reader-panel__hint">
        Busca por referencia (libro, capítulo y versículo) o por palabra del texto.
      </p>

      {results.length > 0 && !chapter ? (
        <ul className="space-y-2">
          {results.map((r) => (
            <li key={r.id} className="bible-reader-panel__search-result">
              <button type="button" className="bible-reader-panel__search-hit" onClick={() => openSearchHit(r)}>
                <p>{r.reference}</p>
                <p>{r.text}</p>
              </button>
              <button
                type="button"
                className="bible-reader-panel__fav mt-2"
                onClick={() => toggleFavorite(r.id)}
              >
                <Star className="inline h-3 w-3" /> Favorito
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {showBooks ? (
        booksLoading ? (
          <p className="bible-reader-panel__verse">Cargando libros…</p>
        ) : booksError ? (
          <p className="bible-reader-panel__verse">{booksError}</p>
        ) : books.length ? (
          <div className="bible-reader-panel__canon">
            {oldTestament.length ? (
              <section>
                <h3 className="bible-reader-panel__testament">Antiguo Testamento</h3>
                {renderBookGrid(oldTestament)}
              </section>
            ) : null}
            {newTestament.length ? (
              <section>
                <h3 className="bible-reader-panel__testament">Nuevo Testamento</h3>
                {renderBookGrid(newTestament)}
              </section>
            ) : null}
          </div>
        ) : (
          <p className="bible-reader-panel__verse">
            No hay libros importados. Ejecuta <code>npm run spiritual:import-bible-full</code> en el servidor.
          </p>
        )
      ) : null}

      {showChapters ? (
        <div>
          <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
            <h3 className="font-semibold">
              {selectedBook.testament === 'NT' ? 'Nuevo Testamento' : 'Antiguo Testamento'}
              {' · '}
              {selectedBook.name}
              {' · '}
              {chapterCount} capítulos
            </h3>
            <button type="button" className="btn-secondary text-sm" onClick={goToBooks}>
              ← Libros
            </button>
          </div>
          <div className="bible-reader-panel__chapter-grid">
            {Array.from({ length: chapterCount }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                className="bible-reader-panel__chapter-btn"
                onClick={() => onSelectChapter(n)}
                disabled={busy}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {showVerses ? (
        <div>
          <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
            <h3 className="font-semibold">
              {chapter.book.testament === 'NT' ? 'Nuevo Testamento' : 'Antiguo Testamento'}
              {' · '}
              {chapter.book.name} {chapter.chapter_number}
              {chapter.chapter_count ? ` de ${chapter.chapter_count}` : ''}
            </h3>
            <div className="flex gap-2 flex-wrap">
              <button type="button" className="btn-secondary text-sm" onClick={goToBooks}>
                Libros
              </button>
              <button type="button" className="btn-secondary text-sm" onClick={goToChapters}>
                Capítulos
              </button>
              <button
                type="button"
                className="btn-secondary text-sm"
                disabled={chapterNum <= 1 || busy}
                onClick={() => loadChapter(selectedBook, chapterNum - 1)}
              >
                ←
              </button>
              <button
                type="button"
                className="btn-secondary text-sm"
                disabled={busy || (chapter.chapter_count ? chapterNum >= chapter.chapter_count : false)}
                onClick={() => loadChapter(selectedBook, chapterNum + 1)}
              >
                →
              </button>
            </div>
          </div>
          <div className="bible-reader-panel__chapter">
            {chapter.verses.map((v) => (
              <span
                key={v.id}
                ref={(el) => { verseRefs.current[v.verse_number] = el; }}
                className={`bible-reader-panel__verse${highlightVerse === v.verse_number ? ' is-highlight' : ''}`}
              >
                <span className="bible-reader-panel__verse-num">{v.verse_number}</span>
                <span className="bible-reader-panel__verse-text">{v.text}</span>
                <button
                  type="button"
                  className="bible-reader-panel__fav"
                  onClick={() => toggleFavorite(v.id)}
                  aria-label="Favorito"
                >
                  <Star className="inline h-3 w-3" />
                </button>
                {' '}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
